import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { estimateFromEntry, formatMoney, formatThaiDate } from '../utils/coopEstimate';
import './CoopSavingsWidget.css';

/**
 * CoopSavingsWidget — การ์ดยอดสหกรณ์ออมทรัพย์ในกริด summary-cards ของหน้าหลัก
 * แสดงการ์ดตามประเภทบัญชีที่บันทึกไว้: หุ้นสหกรณ์ (ประมาณการ), เงินฝากออมทรัพย์พิเศษ, ยอดกู้ (ประมาณการคงเหลือ)
 * ไม่มีข้อมูล = ไม่แสดงอะไรเลย
 */

const EyeButton = ({ hidden, onToggle }) => (
    <button
        type="button"
        className="summary-share-btn coop-card-eye"
        onClick={onToggle}
        title={hidden ? 'แสดงยอด' : 'ซ่อนยอด'}
        aria-label={hidden ? 'แสดงยอด' : 'ซ่อนยอด'}
    >
        {hidden ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
        ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
        )}
    </button>
);

function CoopSavingsWidget() {
    const [entries, setEntries] = useState([]);
    const [hidden, setHidden] = useState(() => {
        try { return sessionStorage.getItem('imoney_coop_widget_amount_hidden') === '1'; } catch (e) { return false; }
    });

    useEffect(() => {
        let cancelled = false;
        api.get('/coop/me')
            .then(r => { if (!cancelled) setEntries(r.data.entries || []); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    const typeOf = (e) => (e.account_type === 'special' || e.account_type === 'loan' ? e.account_type : 'share');
    const latestShare = entries.find(e => typeOf(e) === 'share') || null;
    const latestSpecial = entries.find(e => typeOf(e) === 'special') || null;
    const latestLoan = entries.find(e => typeOf(e) === 'loan') || null;

    if (!latestShare && !latestSpecial && !latestLoan) return null;

    const estShare = estimateFromEntry(latestShare);
    const estLoan = estimateFromEntry(latestLoan);

    const toggleHidden = () => {
        const v = !hidden;
        setHidden(v);
        try { sessionStorage.setItem('imoney_coop_widget_amount_hidden', v ? '1' : '0'); } catch (e) {}
    };

    const money = (n) => (hidden ? '••••••' : formatMoney(n));

    return (
        <>
            {latestShare && (
                <div className="summary-card coop">
                    <div className="summary-icon">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                        </svg>
                    </div>
                    <div className="summary-content">
                        <span className="summary-label">หุ้นสหกรณ์ (ประมาณการวันนี้)</span>
                        <span className="summary-value">{money(estShare.estimated)}</span>
                        <span className="summary-unit">บาท</span>
                        <span className="coop-card-note">
                            {estShare.hasMonthly
                                ? <>ยืนยัน ณ {formatThaiDate(latestShare.confirmed_date)} + {estShare.months} เดือน</>
                                : <>ตามหนังสือ ณ {formatThaiDate(latestShare.confirmed_date)}</>}
                            {' · '}<Link to="/coop" className="coop-card-link">รายละเอียด</Link>
                        </span>
                    </div>
                    <EyeButton hidden={hidden} onToggle={toggleHidden} />
                </div>
            )}

            {latestSpecial && (
                <div className="summary-card coop-special">
                    <div className="summary-icon">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M11.5 1L2 6v2h19V6l-9.5-5zM4 10v7h3v-7H4zm6.5 0v7h3v-7h-3zM17 10v7h3v-7h-3zM2 19v2h19v-2H2z"/>
                        </svg>
                    </div>
                    <div className="summary-content">
                        <span className="summary-label">เงินฝากออมทรัพย์พิเศษ</span>
                        <span className="summary-value">{money(latestSpecial.balance)}</span>
                        <span className="summary-unit">บาท</span>
                        <span className="coop-card-note">
                            ตามหนังสือ ณ {formatThaiDate(latestSpecial.confirmed_date)}
                            {' · '}<Link to="/coop" className="coop-card-link">รายละเอียด</Link>
                        </span>
                    </div>
                    {/* ปุ่มตาแสดงเฉพาะการ์ดแรก — กดครั้งเดียวซ่อนทุกยอดสหกรณ์ */}
                    {!latestShare && <EyeButton hidden={hidden} onToggle={toggleHidden} />}
                </div>
            )}

            {latestLoan && (
                <div className="summary-card coop-loan">
                    <div className="summary-icon">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm1 13h-2v2h-2v-2H9v-2h2v-1.5H9.5c-.83 0-1.5-.67-1.5-1.5v-1c0-.83.67-1.5 1.5-1.5H11V6h2v1.5h2v2h-2V11h1.5c.83 0 1.5.67 1.5 1.5v1c0 .83-.67 1.5-1.5 1.5zM13 9V3.5L18.5 9H13z"/>
                        </svg>
                    </div>
                    <div className="summary-content">
                        <span className="summary-label">ยอดกู้สหกรณ์คงเหลือ (ประมาณการ)</span>
                        <span className="summary-value">{money(estLoan.estimated)}</span>
                        <span className="summary-unit">บาท</span>
                        <span className="coop-card-note">
                            {estLoan.hasMonthly
                                ? (estLoan.estimated > 0
                                    ? <>หักเดือนละ {money(estLoan.monthly)} · เหลือ ~{estLoan.monthsLeft} งวด</>
                                    : <>ผ่อนครบแล้วตามประมาณการ</>)
                                : <>ตามหนังสือ ณ {formatThaiDate(latestLoan.confirmed_date)}</>}
                            {' · '}<Link to="/coop" className="coop-card-link">รายละเอียด</Link>
                        </span>
                    </div>
                    {!latestShare && !latestSpecial && <EyeButton hidden={hidden} onToggle={toggleHidden} />}
                </div>
            )}
        </>
    );
}

export default CoopSavingsWidget;
